import { useEffect, useRef } from "react";
import { 
  Stethoscope, Heart, Award, Users, Smile, Shield, 
  Star, Sparkles, TrendingUp, Activity, Microscope, Bone
} from "lucide-react";
import { branches } from "../data";

export default function About() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    { 
      icon: Stethoscope, 
      title: "Expert Dentists", 
      desc: "Experienced dental professionals", 
      color: "from-blue-500 to-cyan-500",
      glow: "shadow-blue-500/40",
      hoverBg: "hover:shadow-blue-500/40"
    },
    { 
      icon: Activity, 
      title: "Modern Equipment", 
      desc: "Advanced dental technology", 
      color: "from-green-500 to-emerald-500",
      glow: "shadow-green-500/40",
      hoverBg: "hover:shadow-green-500/40"
    },
    { 
      icon: Heart, 
      title: "Personalized Care", 
      desc: "Treatment plans for every patient", 
      color: "from-red-500 to-pink-500",
      glow: "shadow-red-500/40",
      hoverBg: "hover:shadow-red-500/40"
    },
    { 
      icon: Microscope, 
      title: "Advanced Treatments", 
      desc: "Pain-free dental procedures", 
      color: "from-purple-500 to-indigo-500",
      glow: "shadow-purple-500/40",
      hoverBg: "hover:shadow-purple-500/40"
    },
  ];

  const achievements = [
    { 
      number: "5000+", 
      label: "Happy Patients", 
      icon: Smile, 
      color: "from-orange-500 to-red-500",
      glow: "shadow-orange-500/40"
    },
    { 
      number: "13+", 
      label: "Years Experience", 
      icon: Award, 
      color: "from-blue-500 to-cyan-500",
      glow: "shadow-blue-500/40"
    },
    { 
      number: "3", 
      label: "Convenient Branches", 
      icon: Users, 
      color: "from-green-500 to-emerald-500",
      glow: "shadow-green-500/40"
    },
    { 
      number: "Advanced", 
      label: "Dental Technology", 
      icon: Bone, 
      color: "from-purple-500 to-pink-500",
      glow: "shadow-purple-500/40"
    },
  ];

  const branchNames = Object.values(branches).map(b => b.shortName).join(", ");

  return (
    <section ref={sectionRef} id="about" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-br from-white via-[#4a7c59]/5 to-white relative overflow-hidden">
      {/* Subtle Background Elements - No animation */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-[#4a7c59]/10 to-[#2d5a3f]/10 rounded-full filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-blue-100/10 to-cyan-100/10 rounded-full filter blur-3xl opacity-15"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Section Title */}
        <div className="text-center mb-12 animate-on-scroll opacity-0">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#4a7c59]/10 to-[#2d5a3f]/10 rounded-full mb-4">
            <TrendingUp className="w-4 h-4 text-[#4a7c59] mr-2" />
            <span className="text-[#4a7c59] text-sm font-semibold">Our Legacy</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gray-800">About </span>
            <span className="text-gradient-olive">Olive Dental Clinic</span>
          </h2>
          
          <div className="w-24 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mx-auto rounded-full"></div>
        </div>

        {/* Quote Section - No bounce */}
        <div className="animate-on-scroll opacity-0 mb-8">
          <div className="relative p-6 md:p-8 bg-gradient-to-r from-[#4a7c59]/10 to-[#2d5a3f]/10 rounded-2xl border-l-4 border-[#4a7c59] transition-all duration-300 hover:shadow-xl">
            <div className="absolute -top-3 -left-3 w-12 h-12 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-full flex items-center justify-center shadow-lg">
              <Star className="w-6 h-6 text-white fill-white" />
            </div>
            
            <p className="text-gray-700 text-lg md:text-xl leading-relaxed text-center font-medium px-4">
              Since <span className="font-bold text-[#4a7c59]">2012</span>, Olive Dental Clinic has been committed to providing quality dental care with modern technology and a patient-first approach. We proudly serve families across <span className="font-semibold text-[#4a7c59]">{branchNames}</span> with comprehensive dental treatments.
            </p>
            
            <div className="mt-6 flex flex-col items-center justify-center gap-2">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#4a7c59]"></div>
              <span className="text-[#4a7c59] font-semibold tracking-wide">Olive Dental Clinic</span>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#4a7c59]"></div>
              <p className="text-xs text-gray-400 mt-1">✨ Where beautiful smiles begin ✨</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="animate-on-scroll opacity-0 mb-10 text-center">
          <p className="text-gray-600 leading-relaxed text-base md:text-lg">
            Olive Dental Clinic is a trusted dental clinic serving <span className="font-semibold text-[#4a7c59]">Mangalam</span>, <span className="font-semibold text-[#4a7c59]">Koottayi</span> and <span className="font-semibold text-[#4a7c59]">Vettom</span> in Malappuram. Our experienced dentists provide preventive, restorative and cosmetic dental treatments using advanced technology in a comfortable and patient-friendly environment.
          </p>
        </div>

        {/* Features Grid - Premium Style */}
        <div className="animate-on-scroll opacity-0 mb-10">
          <h3 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Why Choose <span className="text-[#4a7c59]">Us?</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="group text-center p-4 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer animate-on-scroll opacity-0"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div 
                  className={`w-14 h-14 mx-auto mb-3
                  bg-gradient-to-r ${feature.color}
                  rounded-xl flex items-center justify-center
                  shadow-md transition-all duration-300
                  group-hover:shadow-2xl ${feature.hoverBg}
                  group-hover:scale-105`}
                >
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-bold text-gray-800 mb-1">{feature.title}</h4>
                <p className="text-xs text-gray-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Achievement Stats - Premium Style */}
        <div className="animate-on-scroll opacity-0 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {achievements.map((item, idx) => (
              <div 
                key={idx} 
                className="group text-center p-4 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer animate-on-scroll opacity-0"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div 
                  className={`w-12 h-12 mx-auto mb-2
                  bg-gradient-to-r ${item.color}
                  rounded-full flex items-center justify-center
                  shadow-md transition-all duration-300
                  group-hover:shadow-2xl ${item.glow}
                  group-hover:scale-105`}
                >
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-800">{item.number}</div>
                <div className="text-xs text-gray-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="animate-on-scroll opacity-0">
          <div className="flex flex-wrap items-center justify-center gap-4 p-4 bg-gradient-to-r from-[#4a7c59]/10 to-[#2d5a3f]/10 rounded-full">
            <Shield className="w-5 h-5 text-[#4a7c59]" />
            {/* <span className="text-sm text-gray-700">Advanced Technology</span>
            <span className="w-1 h-1 bg-[#4a7c59] rounded-full"></span> */}
            <span className="text-sm text-gray-700">Experienced Dentists</span>
            <span className="w-1 h-1 bg-[#4a7c59] rounded-full"></span>
            <span className="text-sm text-gray-700">Patient-Friendly Care</span>
            <Shield className="w-5 h-5 text-[#2d5a3f]" />
          </div>
        </div>

        {/* CTA Button */}
        <div className="animate-on-scroll opacity-0 mt-10 text-center">
          <button 
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-full font-semibold hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            <span>Book an Appointment</span>
            <Sparkles className="w-4 h-4 group-hover:animate-pulse" />
          </button>
        </div>
      </div>
    </section>
  );
}